import React, { useEffect, useState } from "react";
import useTrackWebContext from "../../../hooks/useTrackWebContext";
import instanceCommerce from "../../../config/instanceCommerce.js";
import MetricsList from "../MetricsList/MetricsList";
import MetricsHeader from "../MetricsHeader/MetricsHeader";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import Pagination from "../Pagination/Pagination";
import BranchSelect from "../BranchSelect/BranchSelect";
import { CSVLink } from "react-csv";

const ShopMetrics = () => {
  const {
    dataCommerce,
    token,
    allMetrics,
    search,
    infoCommerce,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setSearch,
    selectedBranch,
    setSelectedBranch
  } = useTrackWebContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [metrics, setMetrics] = useState();
  const [totalPages, setTotalPages] = useState(0);
  const [branch, setBranch] = useState("")
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const userRole = infoCommerce?.rol;
  const isBrand = userRole === "marca";
  const isCommerce = userRole === "comercio";
  const todayDate = new Date();
  const year = todayDate.getFullYear();
  const month = todayDate.getMonth() + 1;
  const day = todayDate.getDate();
  const hour = todayDate.getHours();
  const minute = todayDate.getMinutes();

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        let url = `/api/GetShopActivity?nro_comercio=${dataCommerce}&page=${currentPage}`;

        if (search) {
          if (startDate && endDate) {
            url = `/api/GetShopActivityByDate?nro_comercio=${dataCommerce}&desde=${startDate}&hasta=${endDate}&page=${currentPage}`;
          }
          if (selectedBranch) {
            url += `&id_sucursal=${selectedBranch}`;
          }
        }

        const response = await instanceCommerce({
          url,
          method: "GET",
          timeout: 5000,
          headers: { Authorization: `Bearer ${token}` },
        }).then((res) => res.data);
  
        setMetrics(response.data);
        setTotalPages(
          Math.ceil(parseInt(response.total) / parseInt(response.per_page))
        );
      } catch (error) {
        console.error("Error al obtener métricas:", error);
      }
    };
  
    if (!token || !dataCommerce) return;
    fetchMetrics();
  }, [
    token,
    dataCommerce,
    currentPage,
    search,
    selectedBranch,
    startDate,
    endDate,
  ]);

  const handleSearch = () => {
    setSearch(true); 
    if (branch){
      setSelectedBranch(branch.id_sucursal)
    }
    if(dateRange.startDate && dateRange.endDate){
      setStartDate(dateRange.startDate)
      setEndDate(dateRange.endDate)
    }
    setCurrentPage(1);
  };
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handleClearFilters = () => {
    setSelectedBranch("");
    setBranch("")
    setStartDate("");
    setEndDate("");
    setDateRange({ startDate: "", endDate: "" });
    setSearch(false); 
    setCurrentPage(1); 
  };
  
  const updateDateRange = (key, value) => {
    setDateRange((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <section
      className="
        w-full min-h-[75%] bg-white rounded-md shadow-md mt-4 mb-10 px-4 pt-5 pb-6
        sm:px-5
        md:px-[30px] md:pt-[25px] md:pb-[16px]
        lg:w-[600px] lg:ml-5
        xl:w-[830px] xl:ml-[50px]
      "
    >
      {/* Header */}
      <div className="mb-6 border-b border-b-gray-200 pb-4 md:pb-2">
        <div className="mb-3 md:mb-4">
          <h1 className="font-bold text-xl md:text-2xl text-clvblue leading-snug">
            Historial de ventas registradas
          </h1>
          <p className="text-sm md:text-base text-gray-900">
            Ingresá los datos de búsqueda
          </p>
        </div>

        {/* Filtros */}
        <div className="flex flex-col md:flex-row md:items-end gap-4 py-2">
          <DateRangePicker onSelectDate={updateDateRange} dateRange={dateRange} />
          {isBrand && (
            <BranchSelect onSelectBranch={setBranch} branch={branch} />
          )}
        </div>

        {/* Botones */}
        <div className="flex flex-col md:flex-row justify-end gap-3 mt-4">
          {search && (
            <button
              onClick={handleClearFilters}
              className="rounded-full w-full md:w-40 h-10 text-sm md:text-base bg-clvblue text-white"
            >
              Limpiar filtros
            </button>
          )}
          <button
            onClick={handleSearch}
            className="rounded-full w-full md:w-40 h-10 text-sm md:text-base bg-clvblue text-white"
          >
            Buscar
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="relative overflow-x-auto">
        <table className="w-full text-sm">
          <MetricsHeader isBrand={isBrand} isCommerce={isCommerce} />
          {metrics?.map((item, index) => (
            <MetricsList
              {...item}
              key={index}
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              isBrand={isBrand}
              isCommerce={isCommerce}
            />
          ))}
        </table>
      </div>

      {/* Paginación + CSV */}
      <div className="my-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <Pagination
          currentPage={currentPage}
          onPageChange={handlePageChange}
          totalPages={totalPages}
        />

        <CSVLink
          filename={`VentasRegistradasClubLaVoz_${day}-${month}-${year}__${hour}-${minute}.csv`}
          data={allMetrics || []}
          separator=';'
          className="rounded-full w-full md:w-60 h-10 px-4 text-sm md:text-base md:mt-0 mt-2 font-semibold bg-clvblue text-white text-center flex items-center justify-center"
          >
            Descargar registros
        </CSVLink>
      </div>

      <p className="text-xs md:text-sm text-gray-500 italic text-right leading-4">
        *Podrás descargar un máximo de 500 registros por descarga.
      </p>
    </section>
  );
};

export default ShopMetrics;
